import { useState } from "react";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PasswordInput } from "@/components/PasswordInput";
import { inputCls } from "@/components/AuthLayout";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";

const EMPTY = { current: "", next: "", confirm: "" };

const Field = ({ id, label, value, onChange }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id}>{label}</Label>
    <PasswordInput id={id} testId={id} className={inputCls} value={value} onChange={(e) => onChange(e.target.value)} required />
  </div>
);

export const ChangePasswordButton = ({ className, testId = "change-password-button", iconOnly = false }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF({ ...f, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (f.next.length < 8) return setError("New password must be at least 8 characters.");
    if (f.next !== f.confirm) return setError("New passwords do not match.");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: f.current, new_password: f.next });
      toast.success("Password updated. Use your new password next time you sign in.");
      setF(EMPTY);
      setOpen(false);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); setError(""); setF(EMPTY); }}>
      <button data-testid={testId} onClick={() => setOpen(true)} className={className} aria-label="Change password">
        <KeyRound className={iconOnly ? "h-5 w-5" : "h-3.5 w-3.5"} />
        {!iconOnly && "Change password"}
      </button>
      <DialogContent data-testid="change-password-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Change password</DialogTitle>
          <DialogDescription>Enter your current password, then choose a new one (at least 8 characters).</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field id="current-password-input" label="Current password" value={f.current} onChange={set("current")} />
          <Field id="new-password-input" label="New password" value={f.next} onChange={set("next")} />
          <Field id="confirm-password-input" label="Confirm new password" value={f.confirm} onChange={set("confirm")} />
          {error && <p data-testid="change-password-error" className="text-sm text-red-600">{error}</p>}
          <button
            data-testid="change-password-submit"
            disabled={busy}
            className="w-full rounded-full bg-blue-600 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-60"
          >
            {busy ? "Saving…" : "Update password"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
